
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { FamilyActivityWithUser } from '@/services/activity';
import ActivityIcon from './ActivityIcon';
import { Badge } from '@/components/ui/badge';

interface ActivityItemProps {
  activity: FamilyActivityWithUser;
}

const ActivityItem: React.FC<ActivityItemProps> = ({ activity }) => {
  const navigate = useNavigate();

  const getActivityDescription = () => {
    const { activity_type, entity_name, metadata, user_first_name } = activity;
    
    switch (activity_type) {
      case 'recipe_created':
        return (
          <span>
            <strong>{user_first_name}</strong> created {metadata.is_quick_recipe ? 'a quick recipe' : 'a new recipe'} "{entity_name}"
          </span>
        );
      case 'recipe_updated':
        return (
          <span>
            <strong>{user_first_name}</strong> updated the recipe "{entity_name}"
          </span>
        );
      case 'meal_planned':
        return (
          <span>
            <strong>{user_first_name}</strong> planned "{entity_name}" for {metadata.meal_type} on {metadata.date}
          </span>
        );
      case 'meal_updated':
        return (
          <span>
            <strong>{user_first_name}</strong> updated the {metadata.meal_type} plan for {metadata.date}
          </span>
        );
      case 'feedback_added':
        return (
          <span>
            <strong>{user_first_name}</strong> {metadata.rating ? `rated (${metadata.rating}/5)` : 'commented on'} "{entity_name}"
          </span>
        );
      case 'shopping_list_generated':
        return (
          <span>
            <strong>{user_first_name}</strong> generated a shopping list with {metadata.recipe_count} recipes for {metadata.date_range}
          </span>
        );
      case 'wellness_report_generated':
        return (
          <span>
            <strong>{user_first_name}</strong> generated a wellness report for {metadata.week_start} - {metadata.week_end}
          </span>
        );
      case 'note_created':
        return (
          <span>
            <strong>{user_first_name}</strong> added a note for {metadata.meal_type} on {metadata.date}
          </span>
        );
      case 'note_updated':
        return (
          <span>
            <strong>{user_first_name}</strong> updated a note for {metadata.meal_type} on {metadata.date}
          </span>
        );
      case 'note_deleted':
        return (
          <span>
            <strong>{user_first_name}</strong> deleted a note for {metadata.meal_type} on {metadata.date}
          </span>
        );
      default:
        return (
          <span>
            <strong>{user_first_name}</strong> performed an action on "{entity_name}"
          </span>
        );
    }
  };

  const getActivityBadge = () => {
    const { activity_type, metadata } = activity;
    
    if (activity_type === 'meal_planned' || activity_type === 'meal_updated') {
      return <Badge variant="outline" className="ml-2 text-xs">{metadata.meal_type.charAt(0).toUpperCase() + metadata.meal_type.slice(1)}</Badge>;
    }
    
    if (activity_type === 'recipe_created' && metadata.is_quick_recipe) {
      return <Badge variant="outline" className="ml-2 text-xs">Quick Recipe</Badge>;
    }
    
    if (activity_type === 'feedback_added' && metadata.rating) {
      return <Badge variant="outline" className="ml-2 text-xs">{metadata.rating}/5 ⭐</Badge>;
    }

    if (activity_type === 'shopping_list_generated' && metadata.family_size) {
      return <Badge variant="outline" className="ml-2 text-xs">{metadata.family_size.adults + metadata.family_size.kids} people</Badge>;
    }

    if (activity_type === 'wellness_report_generated' && metadata.avg_wellness_score) {
      return <Badge variant="outline" className="ml-2 text-xs">Score: {metadata.avg_wellness_score.toFixed(1)}</Badge>;
    }

    if (activity_type === 'note_created' || activity_type === 'note_updated' || activity_type === 'note_deleted') {
      return <Badge variant="outline" className="ml-2 text-xs">Note</Badge>;
    }
    
    return null;
  };

  const handleActivityClick = () => {
    const { activity_type, entity_id, metadata } = activity;
    
    switch (activity_type) {
      case 'recipe_created':
      case 'recipe_updated':
        navigate(`/recipe/${entity_id}`);
        break;
      case 'meal_planned':
      case 'meal_updated':
        navigate('/meal-planner');
        break;
      case 'feedback_added':
        if (metadata.recipe_id) {
          navigate(`/recipe/${metadata.recipe_id}#recipe-feedback`);
        }
        break;
      case 'shopping_list_generated':
        navigate('/shopping-list');
        break;
      case 'wellness_report_generated':
        navigate('/wellness');
        break;
      case 'note_created':
      case 'note_updated':
      case 'note_deleted':
        // Navigate to meal planner with the specific date
        if (metadata.date) {
          navigate(`/meal-planner?date=${metadata.date}`);
        } else {
          navigate('/meal-planner');
        }
        break;
      default:
        break;
    }
  };

  const isClickable = ['recipe_created', 'recipe_updated', 'meal_planned', 'meal_updated', 'feedback_added', 'shopping_list_generated', 'wellness_report_generated', 'note_created', 'note_updated', 'note_deleted'].includes(activity.activity_type);

  return (
    <div 
      className={`flex items-start gap-3 p-4 border-b border-gray-100 last:border-b-0 ${isClickable ? 'cursor-pointer hover:bg-gray-50 transition-colors' : ''}`}
      onClick={isClickable ? handleActivityClick : undefined}
    >
      <ActivityIcon activityType={activity.activity_type} />
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-nowrap">
          <p className="text-sm text-gray-900 flex-1 min-w-0">
            {getActivityDescription()}
          </p>
          <div className="flex-shrink-0">
            {getActivityBadge()}
          </div>
        </div>
        
        <p className="text-xs text-gray-500">
          {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
        </p>
      </div>
    </div>
  );
};

export default ActivityItem;
